import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { addToast } from 'components/Toast/Toast'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
    type WatermarkSettingsPayload,
    putWatermarkSettings
} from 'services/endpoints/settings/account/privacyAndPolicy'
import { useSetUserProfile, useUserProfile } from 'store'

type WatermarkField = keyof WatermarkSettingsPayload

interface UpdateWatermarkVariables {
    field: WatermarkField
    value: boolean
}

export const WatermarkSettings = () => {
    const userData = useUserProfile()
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const [watermarkPhotos, setWatermarkPhotos] = useState<boolean>(
        !!userData?.watermark_photos
    )

    const [watermarkVideos, setWatermarkVideos] = useState<boolean>(
        !!userData?.watermark_videos
    )

    /*
     * The profile may load after the component first renders.
     * Keep the toggles synchronized with the latest user profile.
     */
    useEffect(() => {
        setWatermarkPhotos(!!userData?.watermark_photos)
        setWatermarkVideos(!!userData?.watermark_videos)
    }, [
        userData?.watermark_photos,
        userData?.watermark_videos
    ])

    const updateWatermark = useMutation(
        async ({ field, value }: UpdateWatermarkVariables) =>
            await putWatermarkSettings({
                [field]: value
            }),
        {
            onMutate: ({ field, value }: UpdateWatermarkVariables) => {
                /*
                * Keep the shared profile cache synchronized immediately.
                */
                useSetUserProfile({
                    [field]: value
                })
            },
            onError: (
                err,
                variables: UpdateWatermarkVariables
            ) => {
                console.error(err)

                const previousValue = !variables.value

                /*
                * Revert local state.
                */
                if (variables.field === 'watermark_photos') {
                    setWatermarkPhotos(previousValue)
                }

                if (variables.field === 'watermark_videos') {
                    setWatermarkVideos(previousValue)
                }

                /*
                * Revert the shared Zustand profile cache.
                */
                useSetUserProfile({
                    [variables.field]: previousValue
                })

                addToast(
                    'error',
                    t('error:errorSomethingWentWrong')
                )
            },
            onSuccess: () => {
                queryClient.invalidateQueries('user-profile')
            }
        }
    )

    const toggleWatermarkPhotos = () => {
        const nextValue = !watermarkPhotos

        /*
         * Update the UI immediately, then persist it.
         */
        setWatermarkPhotos(nextValue)

        updateWatermark.mutate({
            field: 'watermark_photos',
            value: nextValue
        })
    }

    const toggleWatermarkVideos = () => {
        const nextValue = !watermarkVideos

        /*
         * Update the UI immediately, then persist it.
         */
        setWatermarkVideos(nextValue)

        updateWatermark.mutate({
            field: 'watermark_videos',
            value: nextValue
        })
    }

    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='basic'
                    section={t('privacySecurity')}
                    title={t('watermarkSettings')}
                />
            }
        >
            <ActionCard
                hasSwitch
                toggleFn={toggleWatermarkPhotos}
                toggleActive={watermarkPhotos}
                text={t('watermarkPhotos')}
                /*                     subtext={
                        <span style={{ color: '#778797' }}>
                            {t(
                                'controlWhetherYourProfileCanBeSuggestedToOurUsers'
                            )}
                        </span>
                    } */
                /*                     description={
                        <span style={{ color: '#B0B0B0' }}>
                            {t('note')}:
                            <br />
                            {t('thisFeatureDoesRespectGeoBlocking')}
                        </span>
                    } */
            />

            <ActionCard
                hasSwitch
                toggleFn={toggleWatermarkVideos}
                toggleActive={watermarkVideos}
                text={t('watermarkVideos')}
                /*                     subtext={
                        <span style={{ color: '#778797' }}>
                            {t(
                                'controlWhetherYourProfileCanBeSuggestedToOurUsers'
                            )}
                        </span>
                    }
                    description={
                        <span style={{ color: '#B0B0B0' }}>
                            {t('note')}:
                            <br />
                            {t('thisFeatureDoesRespectGeoBlocking')}
                        </span>
                    } */
            />
        </WithHeaderSection>
    )
}